import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import apiConfig from '@constants/apiConfig';
import { errorCodes } from '@constants/ErrorCode';
import useFetch from '@hooks/useFetch';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import PickupDisplayDeviceForm from '@modules/digibesShop/Devices/tabs/pickupDisplay/PickupDisplayDeviceForm';
import { showErrorMessage } from '@services/notifyService';
import { Button, Flex } from 'antd';

const message = defineMessages({
    saveSuccess: 'Save ReadyBoard successfully',
});

const FORM_ID = 'form-res-pickup-display-device';
const EMPTY = {};

const usePickupDisplayDeviceFormModal = ({ restaurantId, parentId, onReload, onClose } = {}) => {
    const translate = useTranslate();
    const notification = useNotification();

    const [ open, setOpen ] = useState(false);
    const [ detail, setDetail ] = useState(null);
    const [ rawId, setRawId ] = useState(null);
    const [ isEditing, setIsEditing ] = useState(false);
    const [ changed, setChanged ] = useState(false);

    const { execute: executeGetDetail } = useFetch(apiConfig.orderDisplay.getById, { immediate: false });
    const { execute: executeCreate, loading: loadingCreate } = useFetch(apiConfig.orderDisplay.create, {
        immediate: false,
    });
    const { execute: executeUpdate, loading: loadingUpdate } = useFetch(apiConfig.orderDisplay.update, {
        immediate: false,
    });

    const handleSaveError = (err) => {
        const errorCode = err?.response?.data?.code ?? err?.code;
        const errorMessage = errorCodes[errorCode]?.message;
        if (errorMessage) showErrorMessage(translate.formatMessage(errorMessage), translate);
        else showErrorMessage(err?.message || translate.formatMessage(commonMessage.error), translate);
    };

    const closeModal = () => {
        setOpen(false);
        setDetail(null);
        setRawId(null);
        setChanged(false);
        onClose?.();
    };

    const openCreate = () => {
        setIsEditing(false);
        setDetail({});
        setRawId(null);
        setChanged(false);
        setOpen(true);
    };

    const openEdit = (record) => {
        executeGetDetail({
            pathParams: { id: record.id },
            onCompleted: (res) => {
                const data = res?.result === true ? res.data : {};
                setRawId(data?.id);
                setDetail(data?.device ? { ...data.device, layout: data.layout } : {});
                setIsEditing(true);
                setChanged(false);
                setOpen(true);
            },
            onError: () => notification({ type: 'error', message: translate.formatMessage(commonMessage.error) }),
        });
    };

    const onSuccess = () => {
        notification({ message: translate.formatMessage(message.saveSuccess) });
        closeModal();
        onReload?.();
    };

    const handleSubmit = (values) => {
        if (isEditing) {
            executeUpdate({
                data: { ...values, id: rawId, restaurantId, parentId },
                onCompleted: (res) => res?.result === true && onSuccess(),
                onError: handleSaveError,
            });
        } else {
            executeCreate({
                data: { ...values, restaurantId, parentId },
                onCompleted: (res) => res?.result === true && onSuccess(),
                onError: handleSaveError,
            });
        }
    };

    const renderModalFooter = (
        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
            <Button danger onClick={closeModal} icon={<StopOutlined />}>
                {translate.formatMessage(commonMessage.cancel)}
            </Button>
            <Button
                type="primary"
                htmlType="submit"
                form={FORM_ID}
                loading={loadingCreate || loadingUpdate}
                disabled={!changed}
                icon={<SaveOutlined />}
            >
                {isEditing
                    ? translate.formatMessage(commonMessage.update)
                    : translate.formatMessage(commonMessage.create)}
            </Button>
        </Flex>
    );

    const renderModals = () => (
        <BaseModal
            isModalVisible={open}
            isEditing={isEditing}
            handleCancel={closeModal}
            title={translate.formatMessage(commonMessage.devices)}
            width="800px"
            noBodyPadding={true}
            customFooter={renderModalFooter}
        >
            { open && (
                <PickupDisplayDeviceForm
                    formId={FORM_ID}
                    dataDetail={detail || EMPTY}
                    isEditing={isEditing}
                    actions={null}
                    onSubmit={handleSubmit}
                    setIsChangedFormValues={setChanged}
                />)
            }
        </BaseModal>);

    return { openCreate, openEdit, renderModals };
};

export default usePickupDisplayDeviceFormModal;
